import { ResultOf } from "gql.tada";
import { Fragment } from "react";
import { OrgAvatar } from "../components/Avatar";
import { Box } from "../components/Box";
import { ButtonLink } from "../components/Button";
import { FullScreenScrollView } from "../components/FullScreenScrollView";
import { Redirect } from "../components/Redirect";
import { RowLink } from "../components/RowLink";
import { Fill, Space } from "../components/Space";
import { Tag } from "../components/Tag";
import { Text } from "../components/Text";
import { useResponsive } from "../hooks/useResponsive";
import { useTitle } from "../hooks/useTitle";
import { formatDurationFromNow } from "../utils/date";
import { colors, invariantColor } from "../utils/design";
import { graphql, useQuery } from "../utils/graphql";
import { t } from "../utils/i18n";
import { MeQuery } from "../utils/queries";
import { router } from "../utils/routes";

export const OrganizationListQuery = graphql(`
  query OrganizationListQuery {
    organizations {
      id
      name
      slug
      type
      logoUrl
      updatedAt
      owner {
        id
      }
    }
  }
`);

export const OrgListPage: React.FC = () => {
  useTitle(t("page_title.organizations"));
  const deviceSize = useResponsive();
  const isMobile = deviceSize === "mobile";

  const {
    data: { me },
  } = useQuery(MeQuery);
  const {
    data: { organizations },
  } = useQuery(OrganizationListQuery);

  const myOrganizations = organizations.filter((org) => org.owner.id === me.id);
  const joinedOrganizations = organizations.filter((org) => org.owner.id !== me.id);

  const defaultOrganizationSlug = me.defaultOrganization?.slug;

  if (organizations.length === 0) {
    return <Redirect to={router.OrgNewIndex()} />;
  }

  return (
    <FullScreenScrollView maxWidth={535}>
      <Box direction="row" alignItems="center">
        <Text variant="title2" color={invariantColor.white}>
          {t("org_list.title")}
        </Text>

        <Fill minWidth={16} />

        <ButtonLink variant="outline" size="small" icon="plus" to={router.OrgNewIndex()}>
          {isMobile ? t("org_list.new_short") : t("org_list.new")}
        </ButtonLink>
      </Box>

      {myOrganizations.length > 0 && (
        <>
          <Space height={36} />
          <OrgList
            title={t("org_list.my_organizations")}
            organizations={myOrganizations}
            defaultOrganizationSlug={defaultOrganizationSlug}
          />
        </>
      )}

      {joinedOrganizations.length > 0 && (
        <>
          <Space height={36} />
          <OrgList
            title={t("org_list.joined_organizations")}
            organizations={joinedOrganizations}
            defaultOrganizationSlug={defaultOrganizationSlug}
          />
        </>
      )}
    </FullScreenScrollView>
  );
};

type OrgListProps = {
  title: string;
  organizations: ResultOf<typeof OrganizationListQuery>["organizations"];
  defaultOrganizationSlug: string | undefined;
};

const OrgList: React.FC<OrgListProps> = ({ title, organizations, defaultOrganizationSlug }) => {
  return (
    <>
      <Text variant="body" color={colors.neutral[50]}>
        {title}
      </Text>
      <Space height={12} />

      {organizations.map((organization, index) => (
        <Fragment key={organization.id}>
          {index > 0 && <Space height={12} />}
          <RowLink
            title={
              organization.slug === defaultOrganizationSlug ? (
                <>
                  {organization.name}
                  <Space width={8} />
                  <Tag variant="primaryInverse" uppercase={true}>
                    {t("org_list.default")}
                  </Tag>
                </>
              ) : (
                organization.name
              )
            }
            avatar={
              <OrgAvatar
                size="large"
                name={organization.name}
                url={organization.logoUrl}
                updatedAt={organization.updatedAt}
              />
            }
            to={router.OrgHome({ org_slug: organization.slug })}
            description={t("utils.date.updated_at", {
              date: formatDurationFromNow(organization.updatedAt),
            })}
          />
        </Fragment>
      ))}
    </>
  );
};
